import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { zustandStorage } from '@/services/storage'
import type { ThemeMode } from '@/types'

type ResolvedTheme = 'light' | 'dark'

interface ThemeState {
  theme: ThemeMode
  setTheme: (theme: ThemeMode) => void
  toggleTheme: () => void
  resolveTheme: () => ResolvedTheme
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function applyResolvedTheme(resolved: ResolvedTheme) {
  const root = document.documentElement
  root.classList.toggle('dark', resolved === 'dark')
  root.style.colorScheme = resolved
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      setTheme: (theme) => {
        set({ theme })
        const resolved = theme === 'system' ? getSystemTheme() : theme
        applyResolvedTheme(resolved)
      },
      toggleTheme: () => {
        const current = get().theme
        const resolved = current === 'system' ? getSystemTheme() : current
        const next: ThemeMode = resolved === 'dark' ? 'light' : 'dark'
        set({ theme: next })
        applyResolvedTheme(next)
      },
      resolveTheme: () => {
        const theme = get().theme
        return theme === 'system' ? getSystemTheme() : theme
      },
    }),
    {
      name: 'masterhub-theme',
      storage: zustandStorage,
      onRehydrateStorage: () => (state) => {
        if (!state) return
        applyResolvedTheme(state.resolveTheme())
      },
    },
  ),
)
